import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Lock, MessageSquare, Plus, X, ShieldCheck, Calendar, Phone, CheckCircle2, XCircle } from "lucide-react";

/**
 * Turn 3 — Per-tenant SMS Settings (Text Request).
 *
 * Security-first rendering:
 *  - Never renders or accepts an API key. The key is entered directly into
 *    Emergent's encrypted environment secret interface.
 *  - Never renders the webhook token or a complete token-bearing URL.
 *    Only a masked display path + configured/not-configured badge.
 *  - Live-enable toggle and Test Send button remain disabled until the
 *    server reports `contract_verified: true`.
 */
export default function SmsSettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [draftAllowlist, setDraftAllowlist] = useState("");
  // Server-confirmed values for the inputs that save-on-blur. Comparing
  // the blurred value against `cfg` is broken because `onChange` already
  // syncs the input into `cfg` — so blur-equality is always true and the
  // PUT is skipped. The ref is the authoritative "last saved" snapshot.
  const lastSavedRef = useRef({ dashId: "", fromNum: "" });

  const syncLastSaved = (data) => {
    lastSavedRef.current = {
      dashId: data?.text_request_dashboard_id || "",
      fromNum: data?.text_request_from_number || "",
    };
  };

  const load = async () => {
    try {
      const { data } = await api.get("/sms/settings");
      setCfg(data);
      syncLastSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load SMS settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (updates) => {
    setBusy(true);
    try {
      const { data } = await api.put("/sms/settings", updates);
      setCfg(data);
      syncLastSaved(data);
      toast.success("SMS settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const addAllowlistNumber = async () => {
    const raw = draftAllowlist.trim();
    if (!raw) return;
    const next = Array.from(new Set([...(cfg.text_request_test_allowlist || []), raw]));
    await patch({ text_request_test_allowlist: next });
    setDraftAllowlist("");
  };

  const removeAllowlistNumber = async (n) => {
    const next = (cfg.text_request_test_allowlist || []).filter((x) => x !== n);
    await patch({ text_request_test_allowlist: next });
  };

  const runConnectionCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/sms/settings/test-connection");
      if (data.ok) {
        toast.success("Connection check passed");
      } else if (data.status === "pending_contract_verification") {
        toast.warning("Contract verification pending", {
          description: data.detail,
          duration: 8000,
        });
      } else {
        toast.info(data.detail || "Connection check unavailable");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection check failed");
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8 text-sm text-zinc-500" data-testid="sms-settings-loading">
        Loading SMS settings…
      </div>
    );
  }
  if (!cfg) return null;

  const locked = !cfg.contract_verified;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8" data-testid="sms-settings-panel">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
          <MessageSquare className="w-5 h-5 text-[#FF5A00]"/>
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-xl font-bold">SMS delivery (Text Request)</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Per-tenant provider settings. The Text Request API key is <b>never</b> stored here — it is entered directly into Emergent's encrypted environment secret interface.
          </p>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest font-mono-accent px-2 py-1 rounded shrink-0 ${
            cfg.live_effective ? "bg-emerald-100 text-emerald-800" : "bg-yellow-100 text-yellow-800"
          }`}
          data-testid="sms-settings-status-badge"
        >
          {cfg.live_effective ? "Live" : "Mock"}
        </span>
      </div>

      {locked && (
        <div
          className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3"
          data-testid="sms-contract-warning"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"/>
          <div className="text-sm text-amber-900 leading-relaxed">
            <div className="font-semibold mb-1">Contract verification pending</div>
            Live sending, live-enable, and test-send are disabled until Text Request's
            official endpoint, authentication header, request payload, response fields,
            and webhook registration flow are verified. We will not guess these details
            or unlock live mode from third-party documentation.
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label>Provider</Label>
          <Select
            value={cfg.provider}
            onValueChange={(v) => patch({ provider: v })}
            disabled={busy}
          >
            <SelectTrigger className="mt-1" data-testid="sms-provider-select">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mock">Mock (log-only, safe)</SelectItem>
              <SelectItem value="text_request">Text Request</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-zinc-500 mt-1">
            "Mock" logs every send without touching the wire. Provider selection
            is stored per tenant.
          </p>
        </div>

        <div>
          <Label>Text Request Dashboard ID</Label>
          <Input
            type="text"
            value={cfg.text_request_dashboard_id || ""}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== lastSavedRef.current.dashId) {
                patch({ text_request_dashboard_id: v });
              }
            }}
            onChange={(e) => setCfg({ ...cfg, text_request_dashboard_id: e.target.value })}
            placeholder="e.g. 12345"
            className="mt-1"
            data-testid="sms-dashboard-id-input"
          />
          <p className="text-xs text-zinc-500 mt-1">
            Non-secret. Retrieve from your Text Request dashboard once available.
          </p>
        </div>

        <div>
          <Label>Sending phone number</Label>
          <Input
            type="tel"
            value={cfg.text_request_from_number || ""}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== lastSavedRef.current.fromNum) {
                patch({ text_request_from_number: v });
              }
            }}
            onChange={(e) => setCfg({ ...cfg, text_request_from_number: e.target.value })}
            placeholder="+18155156551"
            className="mt-1"
            data-testid="sms-from-number-input"
          />
          <p className="text-xs text-zinc-500 mt-1">
            The phone number your tenant messages will originate from.
          </p>
        </div>

        <div>
          <Label>Live enablement</Label>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              disabled={locked || busy}
              onClick={() => patch({ sms_live_enabled: !cfg.sms_live_enabled })}
              className={`inline-flex items-center gap-2 rounded-full px-4 h-9 text-sm font-medium border transition-colors ${
                cfg.sms_live_enabled && !locked
                  ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
                  : "bg-zinc-100 text-zinc-500 border-zinc-200"
              } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
              title={locked
                ? "Locked until Text Request contract is verified"
                : cfg.sms_live_enabled ? "Live sending armed" : "Currently disabled"}
              data-testid="sms-live-enable-toggle"
            >
              {locked ? <Lock className="w-3.5 h-3.5"/> : <ShieldCheck className="w-3.5 h-3.5"/>}
              {cfg.sms_live_enabled && !locked ? "Live sending armed" : "Live sending disabled"}
            </button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            {locked
              ? "Toggle unlocks after the Text Request API contract is verified."
              : "Persists your intent; effective delivery also requires a configured dashboard ID, sending number, and API key."}
          </p>
        </div>
      </div>

      <div className="mt-6">
        <Label>Test recipient allowlist</Label>
        <p className="text-xs text-zinc-500 mt-1 mb-2">
          Only these numbers may receive test messages. Prevents accidental live sends. Max 20.
        </p>
        <div className="flex flex-wrap gap-2 mb-2" data-testid="sms-allowlist-chips">
          {(cfg.text_request_test_allowlist || []).map((n) => (
            <span
              key={n}
              className="inline-flex items-center gap-2 rounded-full bg-zinc-100 border border-zinc-200 pl-3 pr-1 py-1 text-sm font-mono-accent"
              data-testid={`sms-allowlist-chip-${n}`}
            >
              {n}
              <button
                type="button"
                onClick={() => removeAllowlistNumber(n)}
                className="rounded-full w-6 h-6 flex items-center justify-center hover:bg-zinc-200"
                aria-label={`remove ${n}`}
                data-testid={`sms-allowlist-remove-${n}`}
                disabled={busy}
              >
                <X className="w-3.5 h-3.5"/>
              </button>
            </span>
          ))}
          {(cfg.text_request_test_allowlist || []).length === 0 && (
            <span className="text-xs text-zinc-400">No numbers configured yet.</span>
          )}
        </div>
        <div className="flex gap-2 max-w-md">
          <Input
            type="tel"
            value={draftAllowlist}
            placeholder="+17792519133"
            onChange={(e) => setDraftAllowlist(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); addAllowlistNumber(); }
            }}
            data-testid="sms-allowlist-input"
            disabled={busy}
          />
          <Button
            type="button"
            onClick={addAllowlistNumber}
            disabled={busy || !draftAllowlist.trim()}
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
            data-testid="sms-allowlist-add-btn"
          >
            <Plus className="w-4 h-4 mr-1"/> Add
          </Button>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div>
          <Label>Webhook endpoint</Label>
          <div
            className="mt-1 flex items-center gap-2 text-sm font-mono-accent text-zinc-700 bg-zinc-50 border border-zinc-200 rounded px-3 py-2"
            data-testid="sms-webhook-masked-path"
          >
            <Lock className="w-4 h-4 text-zinc-400 shrink-0"/>
            <span className="truncate">{cfg.webhook_display_path}</span>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            The full URL (including its secret token) is not shown here. A secure
            registration workflow will be added once Text Request's webhook
            requirements are confirmed.
          </p>
          <div className="mt-2 flex items-center gap-2 text-xs">
            <span
              className={`inline-block w-2 h-2 rounded-full ${cfg.webhook_token_configured ? "bg-emerald-500" : "bg-zinc-300"}`}
              data-testid="sms-webhook-status-dot"
            />
            <span data-testid="sms-webhook-status-label">
              {cfg.webhook_token_configured ? "Webhook token configured" : "Webhook token not configured"}
            </span>
          </div>
        </div>

        <div>
          <Label>Connection status</Label>
          <div className="mt-1 space-y-1 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${cfg.api_key_configured ? "bg-emerald-500" : "bg-zinc-300"}`}/>
              API key configured (env-only)
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${cfg.contract_verified ? "bg-emerald-500" : "bg-amber-500"}`}/>
              Contract {cfg.contract_verified ? "verified" : "verification pending"}
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${cfg.live_effective ? "bg-emerald-500" : "bg-zinc-300"}`}/>
              Effective delivery: {cfg.live_effective ? "live" : "mock"}
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={runConnectionCheck}
              disabled={locked || checking}
              title={locked ? "Locked until Text Request contract is verified" : "Verify provider credentials without sending"}
              data-testid="sms-test-connection-btn"
            >
              {locked && <Lock className="w-3.5 h-3.5 mr-1"/>}
              {checking ? "Checking…" : "Test connection"}
            </Button>
            <ReminderPreviewButton />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Reminder Test Harness — pulls `/api/sms/reminders/preview-tomorrow` and
 * shows the exact SMS bodies tomorrow's 5PM-Chicago cron will send.
 * Read-only; nothing is dispatched.
 */
function ReminderPreviewButton() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sms/reminders/preview-tomorrow");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const openDialog = () => { setOpen(true); load(); };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={openDialog}
        data-testid="reminder-preview-btn"
      >
        <Calendar className="w-3.5 h-3.5 mr-1"/>
        Preview tomorrow's reminders
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="reminder-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#FF5A00]"/>
              Reminder preview
              {data && (
                <span className="text-xs font-mono-accent text-zinc-500 ml-auto">
                  Fires {new Date(data.fire_at_utc).toLocaleString()} · Jobs on {data.coverage_day}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[560px] overflow-y-auto">
            {loading && <div className="text-sm text-zinc-500 py-6 text-center">Loading preview…</div>}
            {!loading && data && (
              <>
                {!data.enabled && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                    <AlertTriangle className="w-4 h-4 inline mr-1"/>
                    Appointment reminders are disabled on this business. Nothing will fire tomorrow.
                  </div>
                )}
                <div className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500">
                  Will send ({data.will_send.length})
                </div>
                {data.will_send.length === 0 ? (
                  <div className="text-sm text-zinc-500 py-3 text-center bg-zinc-50 rounded-md">
                    No appointment reminders scheduled for the coverage day.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {data.will_send.map((item) => (
                      <li key={item.job_id} className="border border-zinc-200 rounded-lg p-3 bg-emerald-50/40" data-testid={`reminder-preview-send-${item.job_id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600"/>
                          <span className="text-sm font-medium">{item.customer_name || "Customer"}</span>
                          <span className="text-xs text-zinc-500 flex items-center gap-1 font-mono-accent">
                            <Phone className="w-3 h-3"/>{item.to_number}
                          </span>
                          <span className="ml-auto text-[10px] font-mono-accent text-zinc-400">
                            {item.scheduled_start ? new Date(item.scheduled_start).toLocaleString() : ""}
                          </span>
                        </div>
                        <div className="text-sm bg-white border border-zinc-200 rounded px-3 py-2 whitespace-pre-wrap font-mono-accent text-zinc-700">
                          {item.rendered_body}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {data.will_skip.length > 0 && (
                  <>
                    <div className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 pt-4">
                      Will skip ({data.will_skip.length})
                    </div>
                    <ul className="space-y-2">
                      {data.will_skip.map((item) => (
                        <li key={item.job_id} className="border border-zinc-200 rounded-lg p-3 bg-rose-50/40" data-testid={`reminder-preview-skip-${item.job_id}`}>
                          <div className="flex items-center gap-2">
                            <XCircle className="w-4 h-4 text-rose-500"/>
                            <span className="text-sm font-medium">{item.customer_name || "Customer"}</span>
                            <span className="ml-2 text-[10px] font-mono-accent uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                              {item.skip_reason}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
